<template>
    <div class="activix-datepicker__time-overlay flex h-full min-h-0 flex-col">
        <div
            data-testid="datepicker-time-date-header"
            class="mb-2 grid grid-cols-[2rem_minmax(0,1fr)_2rem] items-center gap-2 text-sm font-semibold text-gray-800"
            v-if="selectedDateText"
        >
            <button
                type="button"
                data-testid="datepicker-time-date-previous"
                class="flex size-8 items-center justify-center rounded text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                :aria-label="previousDayLabel"
                :disabled="!canShiftPrevious"
                @click="$emit('shiftDate', -1)"
            >
                <activix-icon name="regular/arrow-left-1" class="size-4" />
            </button>
            <span class="truncate text-center">
                {{ selectedDateText }}
            </span>
            <button
                type="button"
                data-testid="datepicker-time-date-next"
                class="flex size-8 items-center justify-center rounded text-gray-700 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                :aria-label="nextDayLabel"
                :disabled="!canShiftNext"
                @click="$emit('shiftDate', 1)"
            >
                <activix-icon name="regular/arrow-right-1" class="size-4" />
            </button>
        </div>

        <div
            data-testid="datepicker-time-slot-grid"
            class="grid min-h-0 grow grid-cols-4 gap-1 overflow-y-auto px-1 py-2"
            v-if="timeSlotGrid"
        >
            <button
                type="button"
                data-testid="datepicker-time-slot"
                class="rounded px-2 py-1 text-sm transition-colors"
                :class="
                    isSelectedTimeSlot(slot)
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 hover:bg-blue-500 hover:text-white'
                "
                :data-time-slot-value="slot.value"
                :data-selected="isSelectedTimeSlot(slot)"
                :aria-pressed="isSelectedTimeSlot(slot)"
                :key="slot.value"
                @click="$emit('selectSlot', slot)"
                v-for="slot in timeSlots"
            >
                {{ slot.label }}
            </button>
        </div>

        <div
            data-testid="datepicker-time-spinner"
            class="flex grow items-center justify-center gap-3 py-4 text-gray-900"
            v-else
        >
            <div class="flex flex-col items-center gap-1">
                <button
                    type="button"
                    data-testid="datepicker-time-hour-increment"
                    class="flex size-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                    @click="$emit('adjustHours', 1)"
                >
                    <activix-icon name="regular/arrow-up-1" class="size-4" />
                </button>
                <span
                    data-testid="datepicker-time-hour"
                    class="min-w-12 text-center text-3xl font-normal leading-none"
                >
                    {{ displayHour }}
                </span>
                <button
                    type="button"
                    data-testid="datepicker-time-hour-decrement"
                    class="flex size-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                    @click="$emit('adjustHours', -1)"
                >
                    <activix-icon name="regular/arrow-down-1" class="size-4" />
                </button>
            </div>

            <span class="pb-8 text-3xl leading-none">:</span>

            <div class="flex flex-col items-center gap-1">
                <button
                    type="button"
                    data-testid="datepicker-time-minute-increment"
                    class="flex size-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                    @click="$emit('adjustMinutes', 1)"
                >
                    <activix-icon name="regular/arrow-up-1" class="size-4" />
                </button>
                <span
                    data-testid="datepicker-time-minute"
                    class="min-w-12 text-center text-3xl font-normal leading-none"
                >
                    {{ displayMinute }}
                </span>
                <button
                    type="button"
                    data-testid="datepicker-time-minute-decrement"
                    class="flex size-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
                    @click="$emit('adjustMinutes', -1)"
                >
                    <activix-icon name="regular/arrow-down-1" class="size-4" />
                </button>
            </div>

            <button
                type="button"
                data-testid="datepicker-time-period"
                class="rounded bg-blue-500 px-3 py-2 text-sm font-semibold text-white"
                @click="$emit('togglePeriod')"
                v-if="!uses24HourTime"
            >
                {{ periodLabel }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    /**
     * Presentational time-picker overlay for the RawDatePicker. It renders
     * either a time-slot grid or an hour/minute spinner (plus an optional day
     * shifter and AM/PM toggle) from the time the parent feeds in, and emits the
     * user's intent. It owns no state: the parent commits the chosen time to the
     * vendor and tracks the pending value.
     */
    import { computed } from 'vue';
    import ActivixIcon from '@/components/elements/ActivixIcon.vue';
    import type { TimeSlot } from '@/components/activix-ui/ActivixDatePicker/constants.ts';

    const props = defineProps<{
        hours: number;
        minutes: number;
        timeSlots: TimeSlot[];
        timeSlotGrid: boolean;
        uses24HourTime: boolean;
        selectedDateText: string;
        previousDayLabel: string;
        nextDayLabel: string;
        canShiftPrevious: boolean;
        canShiftNext: boolean;
    }>();

    defineEmits<{
        selectSlot: [slot: TimeSlot];
        adjustHours: [direction: number];
        adjustMinutes: [direction: number];
        togglePeriod: [];
        shiftDate: [direction: number];
    }>();

    function formatTimeUnit(value: number): string {
        return value.toString().padStart(2, '0');
    }

    const displayHour = computed(() => {
        if (props.uses24HourTime) {
            return formatTimeUnit(props.hours);
        }

        return formatTimeUnit(props.hours % 12 || 12);
    });

    const displayMinute = computed(() => formatTimeUnit(props.minutes));

    const periodLabel = computed(() => {
        // AM/PM is an English-only convention; the 12-hour view only renders for
        // locales that use it, so these labels are intentionally not localized.
        return props.hours >= 12 ? 'PM' : 'AM';
    });

    function isSelectedTimeSlot(slot: TimeSlot): boolean {
        return props.hours === slot.hours && props.minutes === slot.minutes;
    }
</script>
